/**
 * The document themes' web fonts — self-hosted, never fetched from Google.
 *
 * Olivia, 5 Oct (answer B): Fontsource's variable packages, the latin and
 * latin-ext subsets only, `font-display: swap`, loaded only when a document
 * uses a theme that needs them. The app chrome never loads them and keeps its
 * own font stack.
 *
 * The packages' own CSS would register every subset (Cyrillic, Greek,
 * Vietnamese…), so the faces are registered here instead, through the
 * FontFace API, from the two woff2 files we want. The `?url` imports are
 * dynamic: nothing is bundled into the page or requested until a theme asks.
 */

export type ThemeFontKey = "montserrat" | "source-serif-4" | "eb-garamond" | "josefin-sans";

interface FontSpec {
  family: string;
  weight: string;
  files: () => Promise<[latin: string, latinExt: string]>;
}

const urls = (a: Promise<{ default: string }>, b: Promise<{ default: string }>) =>
  Promise.all([a, b]).then(([x, y]) => [x.default, y.default] as [string, string]);

const FONTS: Record<ThemeFontKey, FontSpec> = {
  montserrat: {
    family: "Montserrat Variable",
    weight: "100 900",
    files: () =>
      urls(
        import("@fontsource-variable/montserrat/files/montserrat-latin-wght-normal.woff2?url"),
        import("@fontsource-variable/montserrat/files/montserrat-latin-ext-wght-normal.woff2?url"),
      ),
  },
  "source-serif-4": {
    family: "Source Serif 4 Variable",
    weight: "200 900",
    files: () =>
      urls(
        import("@fontsource-variable/source-serif-4/files/source-serif-4-latin-wght-normal.woff2?url"),
        import("@fontsource-variable/source-serif-4/files/source-serif-4-latin-ext-wght-normal.woff2?url"),
      ),
  },
  "eb-garamond": {
    family: "EB Garamond Variable",
    weight: "400 800",
    files: () =>
      urls(
        import("@fontsource-variable/eb-garamond/files/eb-garamond-latin-wght-normal.woff2?url"),
        import("@fontsource-variable/eb-garamond/files/eb-garamond-latin-ext-wght-normal.woff2?url"),
      ),
  },
  "josefin-sans": {
    family: "Josefin Sans Variable",
    weight: "100 700",
    files: () =>
      urls(
        import("@fontsource-variable/josefin-sans/files/josefin-sans-latin-wght-normal.woff2?url"),
        import("@fontsource-variable/josefin-sans/files/josefin-sans-latin-ext-wght-normal.woff2?url"),
      ),
  },
};

// Fontsource's unicode ranges for the two subsets (the same in all four).
const LATIN =
  "U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD";
const LATIN_EXT =
  "U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF";

/**
 * Which self-hosted font a descriptor's font name means: "Montserrat",
 * "Source Serif Pro" / "Source Serif 4" (the same design as TeX's
 * sourceserifpro), "EB Garamond", "Josefin Sans". Null for anything else
 * (Latin Modern, a system face): nothing to load.
 */
export function themeFontKey(name: string | null | undefined): ThemeFontKey | null {
  const n = (name ?? "").toLowerCase().replace(/variable|[^a-z]/g, "");
  if (n.startsWith("montserrat")) return "montserrat";
  if (n.startsWith("sourceserif")) return "source-serif-4";
  if (n.startsWith("ebgaramond")) return "eb-garamond";
  if (n.startsWith("josefin")) return "josefin-sans";
  return null;
}

/**
 * The CSS font-family for a descriptor's heading or body font: our
 * self-hosted family first, then the descriptor's own stack as the fallback
 * while it loads.
 */
export function themeFontStack(name: string | null | undefined, css: string | null | undefined): string {
  const key = themeFontKey(name);
  const rest = (css ?? "").trim();
  if (!key) return rest || "inherit";
  const ours = `'${FONTS[key].family}'`;
  return rest ? `${ours}, ${rest}` : ours;
}

const loading = new Map<ThemeFontKey, Promise<void>>();

/** Register a theme font's two subsets, once per session. Safe to call often. */
export function loadThemeFont(name: string | null | undefined): Promise<void> {
  const key = themeFontKey(name);
  if (!key) return Promise.resolve();
  const existing = loading.get(key);
  if (existing) return existing;
  const spec = FONTS[key];
  const p = (async () => {
    if (typeof document === "undefined" || typeof FontFace === "undefined" || !document.fonts) return;
    const [latin, latinExt] = await spec.files();
    for (const [url, unicodeRange] of [[latin, LATIN], [latinExt, LATIN_EXT]] as const) {
      const face = new FontFace(spec.family, `url(${url}) format("woff2-variations"), url(${url}) format("woff2")`, {
        weight: spec.weight,
        style: "normal",
        display: "swap",
        unicodeRange,
      });
      document.fonts.add(face);
    }
  })().catch((err) => {
    // The fallback stack still renders; try again on the next request.
    loading.delete(key);
    console.warn("[themes] font failed to load", key, err);
  });
  loading.set(key, p);
  return p;
}
